// ===================================================================
// SHELTER — Rapid Intake Form
// Comprehensive survivor check-in with triage, family linking,
// special needs, and QR code generation
// ===================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Title, TextInput, Select, Switch, Checkbox, Button, Paper,
  SimpleGrid, Stack, Group, Textarea, Slider, Divider, Text, Badge,
  Modal, NumberInput, Combobox, useCombobox,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconUserPlus, IconCheck, IconRefresh, IconAlertTriangle, IconPrinter, IconTags, IconBarcode,
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { SPECIAL_NEEDS_OPTIONS, DIETARY_OPTIONS } from '../../types/shelter';
import type { TriageQuestion } from '../../types/shelter';
import { printPropertyLabelSheet } from '../../lib/printLabels';
import { primary } from '../../lib/colors';
import { formatPhone } from '../../lib/phoneFormat';
import DLScanner from '../../components/scanner/DLScanner';
import type { DLData } from '../../lib/dlParser';

// ── Constants ─────────────────────────────────────────────────

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Non-Binary', label: 'Non-Binary' },
  { value: 'Other', label: 'Other' },
];

// ── Form state ────────────────────────────────────────────────

interface IntakeForm {
  // Basic info (required)
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  phone: string;
  email: string;
  // Address
  homeAddress: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  // Shelter assignment
  shelterId: string;
  cotAssignment: string;
  // Emergency contact
  emergencyContactName: string;
  emergencyContactPhone: string;
  // Special & dietary needs
  specialNeeds: string[];
  dietaryNeeds: string[];
  // Minor status
  isMinor: boolean;
  isUnaccompaniedMinor: boolean;
  guardianId: string;
  // Family linking
  familyMode: 'none' | 'existing' | 'new';
  familyId: string;
  // Notes
  notes: string;
}

const INITIAL_FORM: IntakeForm = {
  firstName: '', lastName: '', dateOfBirth: '', gender: '', phone: '', email: '',
  homeAddress: '', city: '', state: '', zip: '', county: '',
  shelterId: '', cotAssignment: '',
  emergencyContactName: '', emergencyContactPhone: '',
  specialNeeds: [], dietaryNeeds: [],
  isMinor: false, isUnaccompaniedMinor: false, guardianId: '',
  familyMode: 'none', familyId: '',
  notes: '',
};

// ── Component ─────────────────────────────────────────────────

export default function ShelterIntake() {
  const { canEdit } = useModulePermission('shelter');
  const [form, setForm] = useState<IntakeForm>({ ...INITIAL_FORM });
  const [linkedPersonId, setLinkedPersonId] = useState<number | null>(null);
  const [linkedHouseholdId, setLinkedHouseholdId] = useState<number | null>(null);
  const [triageAnswers, setTriageAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Directory person search
  const [dirSearch, setDirSearch] = useState('');
  const [dirResults, setDirResults] = useState<Array<{ record_id: number; firstName: string; lastName: string; phone: string; city: string }>>([]);
  const [dirSearching, setDirSearching] = useState(false);
  const dirSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleDirSearch(q: string) {
    setDirSearch(q);
    if (dirSearchRef.current) clearTimeout(dirSearchRef.current);
    if (!q.trim() || !TABLES.people) { setDirResults([]); return; }
    dirSearchRef.current = setTimeout(async () => {
      setDirSearching(true);
      try {
        const r = await queryRecords(TABLES.people, { search: q.trim(), limit: 8 });
        setDirResults(r.records.map((row: Record<string, unknown>) => ({
          record_id: row.record_id as number,
          firstName:  (row[FIELDS.people.firstName]  ?? '') as string,
          lastName:   (row[FIELDS.people.lastName]   ?? '') as string,
          phone:      (row[FIELDS.people.phone]      ?? '') as string,
          city:       (row[FIELDS.people.city]       ?? '') as string,
        })));
      } catch { /* ignore */ }
      finally { setDirSearching(false); }
    }, 300);
  }

  function prefillFromPerson(p: typeof dirResults[0]) {
    setLinkedPersonId(p.record_id);
    setForm(f => ({
      ...f,
      firstName: p.firstName || f.firstName,
      lastName:  p.lastName  || f.lastName,
      phone:     p.phone     || f.phone,
      city:      p.city      || f.city,
    }));
    setDirSearch(`${p.firstName} ${p.lastName}`);
    setDirResults([]);
    notifications.show({ message: `Linked to directory: ${p.firstName} ${p.lastName}`, color: 'var(--color-success)' });
  }

  const handleDLScan = (data: DLData) => {
    setForm(prev => ({
      ...prev,
      firstName: data.firstName || prev.firstName,
      lastName: data.lastName || prev.lastName,
      dateOfBirth: data.dateOfBirth || prev.dateOfBirth,
      gender: data.sex === 'M' ? 'male' : data.sex === 'F' ? 'female' : prev.gender,
      homeAddress: data.address || prev.homeAddress,
      city: data.city || prev.city,
      state: data.state || prev.state,
      zip: data.zip || prev.zip,
    }));
    notifications.show({
      title: 'License Scanned',
      message: `Auto-filled ${data.firstName} ${data.lastName}. Please verify before submitting.`,
      color: 'var(--color-success)',
    });
  };
  const [intakeSuccess, setIntakeSuccess] = useState<{ name: string; qrCode: string; qrDataUrl: string; shelterId: string; cotAssignment: string; intakeDate: string } | null>(null);

  // ── Address autocomplete (HERE API) ───────────────────────────
  const [addrSuggestions, setAddrSuggestions] = useState<Array<{ label: string; address: { street?: string; city?: string; state?: string; postalCode?: string } }>>([]);
  const addrDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addrCombobox = useCombobox();

  const fetchAddrSuggestions = useCallback(async (q: string) => {
    if (!q || q.length < 3) { setAddrSuggestions([]); return; }
    try {
      const WORKER = import.meta.env.DEV
        ? (import.meta.env.VITE_WORKER_BASE || 'https://ogemc-worker.ogemc.workers.dev')
        : '/api/worker';
      const res = await fetch(`${WORKER}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`);
      if (!res.ok) return;
      const data = await res.json();
      const items = (data.items ?? []) as Array<{ title?: string; address?: { label?: string; street?: string; city?: string; state?: string; postalCode?: string } }>;
      setAddrSuggestions(items.map(item => ({
        label: item.address?.label ?? item.title ?? '',
        address: {
          street: item.address?.street ?? '',
          city: item.address?.city ?? '',
          state: item.address?.state ?? '',
          postalCode: item.address?.postalCode ?? '',
        },
      })).filter(s => s.label));
      addrCombobox.openDropdown();
    } catch { /* ignore */ }
  }, []);

  const handleAddrChange = (value: string) => {
    set('homeAddress', value);
    if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
    addrDebounceRef.current = setTimeout(() => fetchAddrSuggestions(value), 300);
  };

  const selectAddrSuggestion = (item: typeof addrSuggestions[0]) => {
    set('homeAddress', item.address.street ?? item.label);
    if (item.address.city) set('city', item.address.city);
    if (item.address.state) set('state', item.address.state);
    if (item.address.postalCode) set('zip', item.address.postalCode);
    setAddrSuggestions([]);
    addrCombobox.closeDropdown();
  };

  const {
    shelters, survivors, families, triageQuestions,
    loadShelters, loadSurvivors, loadFamilies, loadTriageQuestions,
    createSurvivor, createTriageAnswer, createFamily, updateFamily,
  } = useShelterStore();
  const user = useAuthStore(s => s.user);

  // Load data on mount
  useEffect(() => {
    loadShelters();
    loadSurvivors();
    loadFamilies();
    loadTriageQuestions();
  }, [loadShelters, loadSurvivors, loadFamilies, loadTriageQuestions]);

  const activeQuestions = triageQuestions
    .filter(q => q.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Field updater
  const set = useCallback(<K extends keyof IntakeForm>(key: K, value: IntakeForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  // Toggle array values for checkboxes
  const toggleArrayValue = useCallback((key: 'specialNeeds' | 'dietaryNeeds', value: string) => {
    setForm(prev => {
      const arr = prev[key];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value],
      };
    });
  }, []);

  // Set triage answer
  const setTriageAnswer = useCallback((questionId: string, answer: string) => {
    setTriageAnswers(prev => ({ ...prev, [questionId]: answer }));
  }, []);

  // Validation
  const isValid = form.firstName.trim() && form.lastName.trim() && form.dateOfBirth && form.gender;

  // ── Submit ────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!isValid) {
      notifications.show({ title: 'Missing required fields', message: 'First name, last name, date of birth, and gender are required.', color: 'var(--color-error)' });
      return;
    }
    setSubmitting(true);
    try {
      const qrCode = `SHELTER-SV-${Date.now()}`;
      const intakeTimestamp = new Date().toISOString();
      const intakeBy = user?.email ?? 'unknown';

      // Create survivor record
      const survivorRid = await createSurvivor({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        dateOfBirth: form.dateOfBirth,
        gender: form.gender,
        phone: form.phone.trim(),
        email: form.email.trim(),
        homeAddress: form.homeAddress.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        county: form.county.trim(),
        shelterId: form.shelterId,
        cotAssignment: form.cotAssignment.trim(),
        emergencyContactName: form.emergencyContactName.trim(),
        emergencyContactPhone: form.emergencyContactPhone.trim(),
        specialNeeds: JSON.stringify(form.specialNeeds),
        dietaryNeeds: JSON.stringify(form.dietaryNeeds),
        isMinor: form.isMinor,
        isUnaccompaniedMinor: form.isUnaccompaniedMinor,
        guardianId: form.guardianId,
        qrCode,
        status: 'checked_in',
        intakeTimestamp,
        intakeBy,
        notes: form.notes.trim(),
        // Phase 3 — Directory FKs (only written when field is provisioned)
        ...(FIELDS.survivors.personId    && linkedPersonId    ? { personId:    String(linkedPersonId)    } : {}),
        ...(FIELDS.survivors.householdId && linkedHouseholdId ? { householdId: String(linkedHouseholdId) } : {}),
      });

      // Create triage answers — key must match the UI's q_${q.id} format
      for (const q of activeQuestions) {
        const qKey = `q_${q.id}`;
        const answer = triageAnswers[qKey];
        if (answer !== undefined && answer !== '') {
          const isFlagged = q.flagIfAnswer ? answer === q.flagIfAnswer : false;
          await createTriageAnswer({
            survivorId: String(survivorRid),
            questionId: String(qKey),
            answer,
            isFlagged,
            answeredAt: intakeTimestamp,
            answeredBy: intakeBy,
          });
        }
      }

      // Family linking
      if (form.familyMode === 'existing' && form.familyId) {
        const family = families.find(f => String(f.Rid) === form.familyId);
        if (family) {
          const existingIds: string[] = (() => { try { return JSON.parse(family.memberIds); } catch { return []; } })();
          existingIds.push(String(survivorRid));
          await updateFamily(family.Rid, {
            memberIds: JSON.stringify(existingIds),
            memberCount: existingIds.length,
          });
        }
      } else if (form.familyMode === 'new') {
        await createFamily({
          familyName: form.lastName.trim(),
          memberIds: JSON.stringify([String(survivorRid)]),
          memberCount: 1,
          headOfHouseholdId: String(survivorRid),
        });
      }

      notifications.show({
        title: 'Intake Complete',
        message: `${form.firstName} ${form.lastName} checked in successfully.`,
        color: 'var(--color-success)',
        icon: <IconCheck size={16} />,
      });

      // Show success state with QR code
      const qrDataUrl = await QRCode.toDataURL(qrCode, { width: 200, margin: 1 });
      setIntakeSuccess({
        name: `${form.firstName} ${form.lastName}`,
        qrCode,
        qrDataUrl,
        shelterId: form.shelterId,
        cotAssignment: form.cotAssignment,
        intakeDate: intakeTimestamp,
      });
    } catch (err) {
      console.error('Intake error:', err);
      notifications.show({
        title: 'Intake Failed',
        message: err instanceof Error ? err.message : 'An unexpected error occurred.',
        color: 'var(--color-error)',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Shelter select data ────────────────────────────────────────

  const shelterOptions = shelters
    .filter(s => s.status === 'open' || s.status === 'standby')
    .map(s => ({ value: String(s.Rid), label: `${s.name} (${s.currentPopulation}/${s.capacity})` }));

  const familyOptions = families.map(f => ({
    value: String(f.Rid),
    label: `${f.familyName} (${f.memberCount} members)`,
  }));

  const guardianOptions = survivors
    .filter(s => s.status === 'checked_in' && !s.isMinor)
    .map(s => ({ value: String(s.Rid), label: `${s.firstName} ${s.lastName}` }));

  // ── Print badge from success state ────────────────────────────
  const handlePrintBadge = async () => {
    if (!intakeSuccess) return;
    // Open window immediately (before any await) so popup blocker doesn't intercept
    const w = window.open('', '_blank', 'width=400,height=600');
    if (!w) { notifications.show({ title: 'Popup blocked', message: 'Allow popups for this site to print ID badges.', color: 'var(--color-warning)' }); return; }
    // Claim the document immediately before any async work
    w.document.write('<html><body style="font-family:sans-serif;padding:20px;text-align:center;color:#666">Generating ID badge\u2026</body></html>');
    w.document.close();
    const shelterName = shelterOptions.find(s => s.value === intakeSuccess.shelterId)?.label ?? 'Unassigned';
    const intakeDate = intakeSuccess.intakeDate ? new Date(intakeSuccess.intakeDate).toLocaleDateString() : '';
    let qrImg = intakeSuccess.qrDataUrl;
    if (!qrImg && intakeSuccess.qrCode) {
      qrImg = await QRCode.toDataURL(intakeSuccess.qrCode, { width: 300, margin: 1 });
    }
    w.document.open();
    w.document.write(`<!DOCTYPE html><html><head><title>ID Badge</title><script>window.onload=function(){window.print();}<\/script><style>
      @page { size: 3.5in 2.25in; margin: 0; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', Arial, sans-serif; width: 3.5in; padding: 10px; }
      .header { text-align: center; font-size: 8px; font-weight: 700; color: #2C01D6; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #2C01D6; padding-bottom: 4px; margin-bottom: 6px; }
      .name { text-align: center; font-size: 16px; font-weight: 700; margin-bottom: 4px; }
      .qr { text-align: center; margin: 4px 0; }
      .qr img { width: 150px; height: 150px; }
      .code { text-align: center; font-family: monospace; font-size: 9px; color: #555; margin-bottom: 6px; }
      .info { font-size: 9px; line-height: 1.5; }
      .info .label { font-weight: 700; color: #333; }
      @media print { body { -webkit-print-color-adjust: exact; } }
    </style></head><body>
      <div class="header">OpenGov EMC &mdash; Shelter ID Badge</div>
      <div class="name">${intakeSuccess.name}</div>
      <div class="qr">${qrImg ? `<img src="${qrImg}" />` : ''}</div>
      <div class="code">${intakeSuccess.qrCode}</div>
      <div class="info">
        <span class="label">Shelter:</span> ${shelterName}<br/>
        <span class="label">Cot:</span> ${intakeSuccess.cotAssignment || 'N/A'}<br/>
        <span class="label">Intake:</span> ${intakeDate}
      </div>
    </body></html>`);
    w.document.close();
    w.focus();
  };

  // Print blank property label sheet for the newly checked-in survivor
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [blankLabelCount, setBlankLabelCount] = useState<number>(6);

  const handlePrintLabels = async () => {
    if (!intakeSuccess) return;
    // Open window before any await so popup blocker doesn't intercept
    const w = window.open('', '_blank', 'width=700,height=900');
    if (!w) { notifications.show({ title: 'Popup blocked', message: 'Allow popups for this site to print property labels.', color: 'var(--color-warning)' }); return; }
    await printPropertyLabelSheet(
      intakeSuccess.name,
      intakeSuccess.qrCode,
      [], // no items yet — they'll check in property next
      blankLabelCount,
      w,
    );
    setLabelModalOpen(false);
  };

  const handleNewIntake = () => {
    setIntakeSuccess(null);
    setForm({ ...INITIAL_FORM });
    setTriageAnswers({});
  };

  // ── Render ────────────────────────────────────────────────────

  if (!canEdit) {
    return (
      <Stack gap="md" align="center" mt="xl">
        <Title order={3}>Intake</Title>
        <Text c="dimmed">You do not have permission to perform intake check-ins.</Text>
      </Stack>
    );
  }

  // Success state — show QR code and print option
  if (intakeSuccess) {
    return (
      <Stack gap="md" align="center" mt="xl">
        <IconCheck size={48} color="#07963F" />
        <Title order={3}>Intake Complete</Title>
        <Text size="lg" fw={600}>{intakeSuccess.name}</Text>
        <img src={intakeSuccess.qrDataUrl} alt="QR Code" style={{ width: 200, height: 200 }} />
        <Text size="sm" ff="monospace" c="dimmed">{intakeSuccess.qrCode}</Text>
        <Group>
          <Button leftSection={<IconPrinter size={16} />} onClick={handlePrintBadge} color={primary[800]}>
            Print ID Badge
          </Button>
          <Button leftSection={<IconTags size={16} />} onClick={() => setLabelModalOpen(true)} variant="light" color="#03AC9E">
            Print Property Labels
          </Button>
          <Button variant="light" leftSection={<IconUserPlus size={16} />} onClick={handleNewIntake}>
            New Intake
          </Button>
        </Group>
        <Text size="xs" c="dimmed" maw={400} ta="center">
          Print property labels now so the survivor can tag their belongings.
          Items can also be labeled later from the Property page.
        </Text>

        <Modal opened={labelModalOpen} onClose={() => setLabelModalOpen(false)}
          title="Print Property Labels" centered size="sm">
          <Stack>
            <Text size="sm">
              Print blank labels with <strong>{intakeSuccess.name}</strong>&apos;s QR code.
              The survivor can attach these to their bags, electronics, and other belongings.
            </Text>
            <NumberInput
              label="Number of labels"
              value={blankLabelCount}
              onChange={v => setBlankLabelCount(typeof v === 'number' ? v : 6)}
              min={1} max={30}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setLabelModalOpen(false)}>Cancel</Button>
              <Button leftSection={<IconPrinter size={14} />} onClick={handlePrintLabels} color="#03AC9E">
                Print {blankLabelCount} Labels
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs">
          <IconUserPlus size={24} color={primary[700]} />
          <Title order={3} c="black">Rapid Shelter Intake</Title>
        </Group>
        <Group gap="xs">
          <Button
            variant="light"
            color="var(--color-primary-700)"
            leftSection={<IconBarcode size={16} />}
            onClick={() => setScannerOpen(true)}
          >
            Scan License
          </Button>
          <Button
            variant="subtle"
            color="#939598"
            leftSection={<IconRefresh size={16} />}
            onClick={() => { setForm({ ...INITIAL_FORM }); setTriageAnswers({}); }}
          >
            Clear Form
          </Button>
        </Group>
      </Group>

      <DLScanner opened={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleDLScan} />

      {/* ── Directory Lookup (Phase 3) ────────────────────────────── */}
      {TABLES.people && (
        <Paper p="sm" withBorder style={{ borderColor: linkedPersonId ? '#bbf7d0' : '#e2e8f0', background: linkedPersonId ? '#f0fdf4' : undefined }}>
          <Group gap="xs" mb={linkedPersonId ? 0 : 8}>
            <Text size="xs" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Directory Lookup
            </Text>
            {linkedPersonId && (
              <Badge size="xs" color="var(--color-success)" variant="filled">Linked</Badge>
            )}
          </Group>
          {linkedPersonId ? (
            <Group gap={8}>
              <Text size="sm">{dirSearch}</Text>
              <Button size="compact-xs" variant="subtle" color="#939598"
                onClick={() => { setLinkedPersonId(null); setDirSearch(''); }}>
                Unlink
              </Button>
            </Group>
          ) : (
            <div style={{ position: 'relative' }}>
              <TextInput
                placeholder="Search People Directory by name..."
                value={dirSearch}
                onChange={e => handleDirSearch(e.target.value)}
                size="sm"
                rightSection={dirSearching ? <span style={{ fontSize: 12, color: '#888' }}>…</span> : null}
              />
              {dirResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e4e8', borderRadius: 4, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', zIndex: 100, marginTop: 2 }}>
                  {dirResults.map(p => (
                    <button key={p.record_id} onClick={() => prefillFromPerson(p)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14 }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                    >
                      <span style={{ fontWeight: 500 }}>{p.firstName} {p.lastName}</span>
                      {p.phone && <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>{formatPhone(p.phone)}</span>}
                      {p.city  && <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>{p.city}</span>}
                    </button>
                  ))}
                </div>
              )}
              <Text size="xs" c="dimmed" mt={4}>Optional — link this check-in to a Person record to avoid duplicates and enable cross-module history.</Text>
            </div>
          )}
        </Paper>
      )}

      {/* ── Section 1: Basic Info (Required) ─────────────────────── */}
      <Paper p="md" withBorder>
        <Group gap="xs" mb="sm">
          <Title order={4}>Basic Information</Title>
          <Badge color="var(--color-error)" size="xs" variant="light">Required</Badge>
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
          <TextInput
            label="First Name"
            placeholder="First name"
            value={form.firstName}
            onChange={e => set('firstName', e.target.value)}
            required
          />
          <TextInput
            label="Last Name"
            placeholder="Last name"
            value={form.lastName}
            onChange={e => set('lastName', e.target.value)}
            required
          />
          <TextInput
            label="Date of Birth"
            placeholder="MM/DD/YYYY"
            type="date"
            value={form.dateOfBirth}
            onChange={e => set('dateOfBirth', e.target.value)}
            required
          />
          <Select
            label="Gender"
            placeholder="Select gender"
            data={GENDER_OPTIONS}
            value={form.gender}
            onChange={v => set('gender', v ?? '')}
            required
          />
          <TextInput
            label="Phone"
            placeholder="(555) 123-4567"
            value={form.phone}
            onChange={e => set('phone', formatPhone(e.target.value))}
          />
          <TextInput
            label="Email"
            placeholder="email@example.com"
            type="email"
            value={form.email}
            onChange={e => set('email', e.target.value)}
          />
        </SimpleGrid>
      </Paper>

      {/* ── Section 2: Shelter Assignment ──────────────────────── */}
      <Paper p="md" withBorder>
        <Title order={4} mb="sm">Shelter Assignment</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <Select
            label="Shelter"
            placeholder="Select shelter"
            data={shelterOptions}
            value={form.shelterId}
            onChange={v => set('shelterId', v ?? '')}
            searchable
          />
          <TextInput
            label="Cot Assignment"
            placeholder="e.g., Wing B, Cot 14"
            value={form.cotAssignment}
            onChange={e => set('cotAssignment', e.target.value)}
          />
        </SimpleGrid>
      </Paper>

      {/* ── Section 3: Address ──────────────────────────────────── */}
      <Paper p="md" withBorder>
        <Title order={4} mb="sm">Home Address</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
          <Combobox store={addrCombobox} onOptionSubmit={(val) => {
            const item = addrSuggestions[Number(val)];
            if (item) selectAddrSuggestion(item);
          }} style={{ gridColumn: 'span 2' }}>
            <Combobox.Target>
              <TextInput
                label="Street Address"
                placeholder="123 Main St"
                value={form.homeAddress}
                onChange={e => handleAddrChange(e.target.value)}
                onFocus={() => addrSuggestions.length > 0 && addrCombobox.openDropdown()}
                onBlur={() => setTimeout(() => addrCombobox.closeDropdown(), 200)}
              />
            </Combobox.Target>
            {addrSuggestions.length > 0 && (
              <Combobox.Dropdown>
                <Combobox.Options>
                  {addrSuggestions.map((item, i) => (
                    <Combobox.Option key={i} value={String(i)}>
                      {item.label}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </Combobox.Dropdown>
            )}
          </Combobox>
          <TextInput
            label="City"
            placeholder="City"
            value={form.city}
            onChange={e => set('city', e.target.value)}
          />
          <TextInput
            label="State"
            placeholder="FL"
            value={form.state}
            onChange={e => set('state', e.target.value)}
          />
          <TextInput
            label="ZIP"
            placeholder="32801"
            value={form.zip}
            onChange={e => set('zip', e.target.value)}
          />
          <TextInput
            label="County"
            placeholder="Orange"
            value={form.county}
            onChange={e => set('county', e.target.value)}
          />
        </SimpleGrid>
      </Paper>

      {/* ── Section 4: Emergency Contact ────────────────────────── */}
      <Paper p="md" withBorder>
        <Title order={4} mb="sm">Emergency Contact</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Contact Name"
            placeholder="Full name"
            value={form.emergencyContactName}
            onChange={e => set('emergencyContactName', e.target.value)}
          />
          <TextInput
            label="Contact Phone"
            placeholder="(555) 123-4567"
            value={form.emergencyContactPhone}
            onChange={e => set('emergencyContactPhone', formatPhone(e.target.value))}
          />
        </SimpleGrid>
      </Paper>

      {/* ── Section 5: Special Needs ────────────────────────────── */}
      <Paper p="md" withBorder>
        <Title order={4} mb="sm">Special Needs</Title>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
          {SPECIAL_NEEDS_OPTIONS.map(opt => (
            <Checkbox
              key={opt.value}
              label={opt.label}
              checked={form.specialNeeds.includes(opt.value)}
              onChange={() => toggleArrayValue('specialNeeds', opt.value)}
            />
          ))}
        </SimpleGrid>
      </Paper>

      {/* ── Section 6: Dietary Needs ────────────────────────────── */}
      <Paper p="md" withBorder>
        <Title order={4} mb="sm">Dietary Needs</Title>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
          {DIETARY_OPTIONS.map(opt => (
            <Checkbox
              key={opt.value}
              label={opt.label}
              checked={form.dietaryNeeds.includes(opt.value)}
              onChange={() => toggleArrayValue('dietaryNeeds', opt.value)}
            />
          ))}
        </SimpleGrid>
      </Paper>

      {/* ── Section 7: Minor Status ──────────────────────────────── */}
      <Paper p="md" withBorder>
        <Title order={4} mb="sm">Minor Status</Title>
        <Stack gap="sm">
          <Switch
            label="Is a Minor (under 18)"
            checked={form.isMinor}
            onChange={e => {
              const checked = e.currentTarget.checked;
              set('isMinor', checked);
              if (!checked) {
                set('isUnaccompaniedMinor', false);
                set('guardianId', '');
              }
            }}
          />
          {form.isMinor && (
            <>
              <Switch
                label="Unaccompanied Minor"
                checked={form.isUnaccompaniedMinor}
                onChange={e => set('isUnaccompaniedMinor', e.currentTarget.checked)}
                color="var(--color-error)"
              />
              {form.isUnaccompaniedMinor && (
                <Select
                  label="Assign Guardian (existing checked-in adult)"
                  placeholder="Select guardian"
                  data={guardianOptions}
                  value={form.guardianId}
                  onChange={v => set('guardianId', v ?? '')}
                  searchable
                  clearable
                />
              )}
            </>
          )}
        </Stack>
      </Paper>

      {/* ── Section 8: Triage Questions ──────────────────────────── */}
      {activeQuestions.length > 0 && (
        <Paper p="md" withBorder>
          <Group gap="xs" mb="sm">
            <Title order={4}>Triage Assessment</Title>
            <Badge color="#F86A0B" size="xs" variant="light">{activeQuestions.length} questions</Badge>
          </Group>
          <Stack gap="sm">
            {activeQuestions.map(q => {
              // Use stable question ID as key so React won't reuse DOM between
              // different questions if the list re-orders on re-render
              const qKey = `q_${q.id}`;
              return (
                <TriageField
                  key={qKey}
                  questionKey={qKey}
                  question={q}
                  value={triageAnswers[qKey] ?? ''}
                  onChange={val => setTriageAnswer(qKey, val)}
                />
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* ── Section 9: Family Linking ──────────────────────────── */}
      <Paper p="md" withBorder>
        <Title order={4} mb="sm">Family Linking</Title>
        <Stack gap="sm">
          <Select
            label="Family Assignment"
            data={[
              { value: 'none', label: 'No family group' },
              { value: 'existing', label: 'Join existing family' },
              { value: 'new', label: 'Create new family group' },
            ]}
            value={form.familyMode}
            onChange={v => {
              set('familyMode', (v ?? 'none') as IntakeForm['familyMode']);
              set('familyId', '');
            }}
          />
          {form.familyMode === 'existing' && (
            <Select
              label="Select Family"
              placeholder="Search families"
              data={familyOptions}
              value={form.familyId}
              onChange={v => set('familyId', v ?? '')}
              searchable
            />
          )}
          {form.familyMode === 'new' && (
            <Text size="sm" c="dimmed">
              A new family group will be created using the last name "{form.lastName || '...'}" with this survivor as head of household.
            </Text>
          )}
        </Stack>
      </Paper>

      {/* ── Notes ────────────────────────────────────────────────── */}
      <Paper p="md" withBorder>
        <Title order={4} mb="sm">Additional Notes</Title>
        <Textarea
          placeholder="Any additional intake notes..."
          minRows={3}
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
        />
      </Paper>

      {/* ── Submit ───────────────────────────────────────────────── */}
      <Divider />
      <Group justify="flex-end">
        <Button
          size="lg"
          color={primary[700]}
          leftSection={<IconUserPlus size={20} />}
          onClick={handleSubmit}
          loading={submitting}
          disabled={!isValid}
        >
          Complete Intake & Check In
        </Button>
      </Group>
    </Stack>
  );
}

// ── Triage Field Renderer ──────────────────────────────────────

function TriageField({
  question,
  questionKey,
  value,
  onChange,
}: {
  question: TriageQuestion;
  questionKey: string;
  value: string;
  onChange: (val: string) => void;
}) {
  const baseValue = value.split('|||')[0];
  const detailValue = value.split('|||')[1] || '';
  const flagged = question.flagIfAnswer && baseValue === question.flagIfAnswer;

  const renderInput = () => {
    switch (question.questionType) {
      case 'yes_no':
        return (
          <Switch
            id={`switch-${questionKey}`}
            label={baseValue === 'yes' ? 'Yes' : baseValue === 'no' ? 'No' : 'Not answered'}
            checked={baseValue === 'yes'}
            onChange={e => {
              // Use checked from the event; stopPropagation prevents any
              // parent click handlers from double-firing
              e.stopPropagation();
              const ans = e.currentTarget.checked ? 'yes' : 'no';
              onChange(detailValue ? `${ans}|||${detailValue}` : ans);
            }}
            color={flagged ? 'var(--color-error)' : undefined}
          />
        );

      case 'multiple_choice': {
        const options: string[] = (() => { try { return JSON.parse(question.options); } catch { return []; } })();
        return (
          <Select
            placeholder="Select answer"
            data={options.map(o => ({ value: o, label: o }))}
            value={value || null}
            onChange={v => onChange(v ?? '')}
            clearable
          />
        );
      }

      case 'text':
        return (
          <TextInput
            placeholder="Enter response"
            value={value}
            onChange={e => onChange(e.target.value)}
          />
        );

      case 'scale':
        return (
          <Stack gap={4}>
            <Slider
              min={1}
              max={10}
              step={1}
              value={value ? Number(value) : 5}
              onChange={v => onChange(String(v))}
              marks={[
                { value: 1, label: '1' },
                { value: 5, label: '5' },
                { value: 10, label: '10' },
              ]}
            />
          </Stack>
        );

      default:
        return (
          <TextInput
            placeholder="Enter response"
            value={value}
            onChange={e => onChange(e.target.value)}
          />
        );
    }
  };

  const showDetail = baseValue === 'yes' || flagged;

  return (
    <Paper p="sm" withBorder style={flagged ? { borderColor: '#B12525', backgroundColor: '#fef2f2' } : undefined}>
      <Stack gap="xs">
        <Group gap="xs">
          <Text size="sm" fw={500}>{question.question}</Text>
          {question.category && (
            <Badge size="xs" variant="light" color="var(--color-primary-700)">{question.category}</Badge>
          )}
          {flagged && (
            <Badge size="xs" color="var(--color-error)" leftSection={<IconAlertTriangle size={10} />}>
              Flagged
            </Badge>
          )}
        </Group>
        {renderInput()}
        {showDetail && (
          <TextInput
            placeholder="Please provide details..."
            size="sm"
            value={detailValue}
            onChange={e => onChange(`${baseValue}|||${e.target.value}`)}
            style={{ borderColor: '#B12525' }}
          />
        )}
      </Stack>
    </Paper>
  );
}
